import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { api, getToken } from "@/lib/api";

const TeamContext = createContext(null);
const ACTIVE_KEY = "pc_active_team";
const WS_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/^http/, "ws");

const PRESENCE_COLORS = ["#f59e0b", "#22d3ee", "#a78bfa", "#34d399", "#f472b6", "#fb7185", "#60a5fa"];
export const colorFor = (id) => {
  let h = 0; for (let i = 0; i < (id || "").length; i++) h = (h * 31 + id.charCodeAt(i)) % PRESENCE_COLORS.length;
  return PRESENCE_COLORS[h];
};

const scalarFields = ["you_are", "round_number", "round_name", "team_a_name", "team_b_name", "event_name", "opponent_locked"];

function diffOps(prev, next) {
  const ops = [];
  const pm = prev.matrix || {}, nm = next.matrix || {};
  for (const k of Object.keys(nm)) if (JSON.stringify(nm[k]) !== JSON.stringify(pm[k])) ops.push({ path: `matrix.${k}`, value: nm[k] });
  for (const k of Object.keys(pm)) if (!(k in nm)) ops.push({ path: `matrix.${k}`, value: null, unset: true });
  const pf = prev.faction_ratings || {}, nf = next.faction_ratings || {};
  const pids = new Set([...Object.keys(pf), ...Object.keys(nf)]);
  for (const pid of pids) {
    const a = pf[pid] || {}, b = nf[pid] || {};
    const armies = new Set([...Object.keys(a), ...Object.keys(b)]);
    for (const army of armies) {
      if (JSON.stringify(a[army]) !== JSON.stringify(b[army])) {
        if (b[army] === undefined) ops.push({ path: `faction_ratings.${pid}.${army}`, value: null, unset: true });
        else ops.push({ path: `faction_ratings.${pid}.${army}`, value: b[army] });
      }
    }
  }
  for (const f of scalarFields) if (JSON.stringify(prev[f]) !== JSON.stringify(next[f])) ops.push({ path: f, value: next[f] });
  if (JSON.stringify(prev.players) !== JSON.stringify(next.players)) ops.push({ path: "players", value: next.players });
  if (JSON.stringify(prev.resolver) !== JSON.stringify(next.resolver)) ops.push({ path: "resolver", value: next.resolver });
  return ops;
}

function applyOp(setup, op) {
  const parts = op.path.split(".");
  const top = parts[0];
  const next = { ...setup };
  if (top === "matrix" || top === "faction_ratings") {
    const key = parts.slice(1).join(".");
    if (top === "matrix") {
      const m = { ...(next.matrix || {}) };
      if (op.unset) delete m[key]; else m[key] = op.value;
      next.matrix = m;
    } else {
      const pid = parts[1], army = parts.slice(2).join(".");
      const fr = { ...(next.faction_ratings || {}) };
      fr[pid] = { ...(fr[pid] || {}) };
      if (op.unset) delete fr[pid][army]; else fr[pid][army] = op.value;
      next.faction_ratings = fr;
    }
  } else {
    next[top] = op.value;
  }
  return next;
}

export function TeamProvider({ children }) {
  const [teams, setTeams] = useState([]);
  const [activeId, setActiveId] = useState(localStorage.getItem(ACTIVE_KEY) || "");
  const [setup, setSetup] = useState(null);
  const [presence, setPresence] = useState([]);
  const wsRef = useRef(null);
  const setupRef = useRef(null);
  const saveTimer = useRef();
  const pendingOps = useRef([]);

  setupRef.current = setup;
  const activeTeam = teams.find((t) => t.id === activeId) || null;

  const loadTeams = useCallback(async () => {
    const ts = await api.teams();
    setTeams(ts);
    setActiveId((cur) => {
      if (cur && ts.some((t) => t.id === cur)) return cur;
      return ts[0]?.id || "";
    });
    return ts;
  }, []);

  useEffect(() => { loadTeams().catch(() => {}); }, [loadTeams]);

  // load setup + open websocket when active team changes
  useEffect(() => {
    if (!activeId) { setSetup(null); return; }
    localStorage.setItem(ACTIVE_KEY, activeId);
    let alive = true;
    api.teamSetup(activeId).then((s) => alive && setSetup(s)).catch(() => {});

    const ws = new WebSocket(`${WS_BASE}/api/ws/team/${activeId}?token=${getToken()}`);
    wsRef.current = ws;
    ws.onmessage = (ev) => {
      const msg = JSON.parse(ev.data);
      if (msg.type === "presence") setPresence(msg.users || []);
      else if (msg.type === "reload") api.teamSetup(activeId).then(setSetup).catch(() => {});
      else if (msg.type === "ops") {
        setSetup((cur) => { let s = cur; for (const op of msg.ops || []) s = applyOp(s, op); return s; });
      }
    };
    ws.onclose = () => { if (wsRef.current === ws) wsRef.current = null; };
    return () => { alive = false; try { ws.close(); } catch (e) { console.debug("ws close", e); } };
  }, [activeId]);

  const flush = useCallback(() => {
    const ops = pendingOps.current;
    pendingOps.current = [];
    if (ops.length && activeId) api.teamPatch(activeId, ops).catch(() => {});
  }, [activeId]);

  const updateSetup = useCallback((ns) => {
    const prev = setupRef.current;
    setSetup(ns);
    if (prev) {
      const ops = diffOps(prev, ns);
      if (ops.length) {
        pendingOps.current.push(...ops);
        clearTimeout(saveTimer.current);
        saveTimer.current = setTimeout(flush, 300);
      }
    }
  }, [flush]);

  const replaceSetup = useCallback((doc) => setSetup(doc), []);

  const sendFocus = useCallback((cell) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === 1) ws.send(JSON.stringify({ type: "focus", cell }));
  }, []);

  return (
    <TeamContext.Provider value={{
      teams, activeId, setActiveId, activeTeam, loadTeams,
      setup, updateSetup, replaceSetup, presence, sendFocus,
    }}>
      {children}
    </TeamContext.Provider>
  );
}

export const useTeam = () => useContext(TeamContext);
